import { Required } from 'utility-types';
import { ModeName } from '@commutifi/modes';
import { type CommuteProfile, type CommuteCost, CommuteCostFrequency, DaysValueEnum, Metric, RouteLegWithMetrics, Score, Vehicle, PreferredLegShape, CommuteProfileLegWithTransit } from 'shared/interfaces/Commute';
import { GetResponse, RequestParams } from 'shared/interfaces/Api';
import { apiCall, dashboardApiServer } from '../../index';
import endpoints from './endpoints';
const apiDashboardServer = apiCall(dashboardApiServer);
export { type CommuteProfile, type CommuteCost, CommuteCostFrequency };
export interface CommuteProfileProcessed extends Required<CommuteProfile, 'id'> {
  isReturn: boolean;
  orderedModes: ModeName[];
  scores: Score;
  metrics: {
    daily: Omit<Metric, 'carbonEmissions'> & {
      carbon: number;
    };
    monthly: Omit<Metric, 'carbonEmissions'> & {
      carbon: number;
    };
  };
  goingLegs?: (RouteLegWithMetrics & {
    polyline?: string | null;
  })[];
  returnLegs?: (RouteLegWithMetrics & {
    polyline?: string | null;
  })[];
  vehicleId?: string | null;
  vehicle?: Partial<Vehicle>;
  legs: CommuteProfileLegWithTransit[];
  preferredShape?: PreferredLegShape;
}
export type PostCommuteProfileBody = (Omit<CommuteProfile, 'id' | 'isReturn'> & {
  commuteCosts?: CommuteCost[];
})
// Or if we send 'assumeMissingValues' and 'assumedTimestampRef' query params we will omit more details
| Omit<CommuteProfile, 'id' | 'isReturn' | 'arriveOfficeTime' | 'leaveOfficeTime' | 'weeklyFrequency'>;
export interface PostCommuteProfileQueryParams {
  assumeMissingValues?: boolean;
  assumedTimestampRef?: string;
}
interface PutCommuteProfileBody extends CommuteProfileProcessed {
  commuteCosts?: CommuteCost[];
}

// --- GET ---
export const fetchCommuteProfiles = (queryParams: {
  accountId?: string | null;
} & RequestParams = {}): Promise<GetResponse<CommuteProfileProcessed>> => apiDashboardServer(endpoints.GET.CommuteProfiles.route(), {
  method: 'get',
  queryParams
});
export const fetchCommuteProfile = (commuteProfileId: string, queryParams: RequestParams = {}): Promise<CommuteProfileProcessed> => apiDashboardServer(endpoints.GET.CommuteProfileSummary.route(commuteProfileId), {
  method: 'get',
  queryParams
});

// --- UPDATE ---
export interface PatchRequestParams {
  id: string;
  previousId?: string;
  day: DaysValueEnum | undefined;
  data: Partial<CommuteProfile>;
}
export const patchCommuteProfile = (params: PatchRequestParams) => apiDashboardServer(endpoints.PATCH.CommuteProfile.route(params.id), {
  method: 'patch',
  data: params.data
});
export interface PutRequestParams {
  data: PutCommuteProfileBody;
}
export const putCommuteProfile = (params: PutRequestParams): Promise<CommuteProfile> => apiDashboardServer(endpoints.PUT.CommuteProfile.route(params.data.id), {
  method: 'put',
  data: params.data
});

// --- POST ---
export const postCommuteProfile = ({
  body: data,
  queryParams
}: {
  body: PostCommuteProfileBody;
  queryParams?: PostCommuteProfileQueryParams;
}): Promise<CommuteProfile> => apiDashboardServer(endpoints.POST.CommuteProfiles.route(), {
  method: 'post',
  queryParams,
  data
});

// --- DELETE ---
export const deleteCommuteProfile = (commuteProfileId: string) => apiDashboardServer(endpoints.DELETE.CommuteProfile.route(commuteProfileId), {
  method: 'delete'
});